import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  useToast
} from "@chakra-ui/react";
import {
  FaCamera,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaCopy,
  FaGlobeAmericas,
  FaRobot,
  FaSave
} from "react-icons/fa";
import { api } from "../api";

const sortByCanal = (list) =>
  [...list].sort((a, b) => Number(a?.canal || 0) - Number(b?.canal || 0));

const formatHora = (value) => {
  if (!value) return "";
  return String(value).slice(0, 5);
};

const GerenciarCameras = ({ usuarioId }) => {
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const toast = useToast();

  const fetchCameras = useCallback(async () => {
    if (!usuarioId) return;
    setLoading(true);
    try {
      const response = await api.get(`/cameras/usuario/${usuarioId}`);
      const list = Array.isArray(response.data) ? response.data : [];
      setCameras(sortByCanal(list));
    } catch (error) {
      toast({
        title: "Erro ao carregar câmeras",
        status: "error",
        duration: 3000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  }, [usuarioId, toast]);

  useEffect(() => {
    fetchCameras();
  }, [fetchCameras]);

  const stats = useMemo(() => {
    const total = cameras.length;
    const ativas = cameras.filter((cam) => Number(cam.ativa) === 1).length;
    const publicas = cameras.filter((cam) => Number(cam.flag_publico) === 1).length;
    const iaAtivas = cameras.filter((cam) => Number(cam.ia_ativo) === 1).length;
    return { total, ativas, publicas, iaAtivas };
  }, [cameras]);

  const statItems = useMemo(
    () => [
      { key: "total", label: "Total", value: stats.total, icon: FaCamera },
      { key: "ativas", label: "Ativas", value: stats.ativas, icon: FaCheckCircle },
      { key: "publicas", label: "Públicas", value: stats.publicas, icon: FaGlobeAmericas },
      { key: "ia", label: "IA ativa", value: stats.iaAtivas, icon: FaRobot }
    ],
    [stats]
  );

  const handleChange = (id, field, value) => {
    setCameras((prev) => prev.map((cam) => (cam.id === id ? { ...cam, [field]: value } : cam)));
  };

  const handleUpdate = async (camera) => {
    setSavingId(camera.id);
    try {
      await api.put(`/cameras/editar/${camera.id}`, {
        nome: camera.nome,
        ativa: camera.ativa,
        flag_publico: camera.flag_publico,
        ia_ativo: camera.ia_ativo ? 1 : 0,
        ia_hora_inicio: camera.ia_hora_inicio || null,
        ia_hora_fim: camera.ia_hora_fim || null
      });

      toast({
        title: `Câmera ${camera.canal || camera.id} atualizada`,
        status: "success",
        duration: 2200,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: "Erro ao atualizar camera",
        status: "error",
        duration: 3000,
        isClosable: true
      });
    } finally {
      setSavingId(null);
    }
  };

  const copyRtmp = async (rtmpLink) => {
    if (!rtmpLink) {
      toast({
        title: "RTMP nao disponivel",
        status: "warning",
        duration: 2000,
        isClosable: true
      });
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(rtmpLink);
      } else {
        const input = document.createElement("textarea");
        input.value = rtmpLink;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }

      toast({
        title: "RTMP copiado",
        status: "success",
        duration: 1800,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: "Não foi possível copiar",
        status: "error",
        duration: 2200,
        isClosable: true
      });
    }
  };

  const toggleExpand = (cameraId) => {
    setExpandedId((prev) => (prev === cameraId ? null : cameraId));
  };

  return (
    <Box px={{ base: 3, md: 5 }} py={{ base: 3, md: 4 }} className="cameras-page">
      <Stack spacing={4}>
        <Box className="cameras-shell">
          <Stack spacing={2}>
            <Text className="cameras-title">Gerenciar câmeras</Text>
            <Text className="cameras-subtitle">
              Câmeras do plano já provisionadas. Aqui você apenas configura.
            </Text>
          </Stack>

          <Box className="camera-summary-grid" mt={3}>
            {statItems.map((item) => {
              const Icon = item.icon;
              return (
                <Box key={item.key} className="camera-summary-card">
                  <HStack spacing={1.5} justify="space-between" align="start">
                    <Stack spacing={0.5}>
                      <Text className="camera-summary-label">{item.label}</Text>
                      <Text className="camera-summary-value">{item.value}</Text>
                    </Stack>
                    <Box className="camera-summary-icon">
                      <Icon />
                    </Box>
                  </HStack>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box className="cameras-shell">
          <HStack justify="space-between" align="center" mb={2}>
            <Text className="cameras-section-title">Editar e configurar câmeras</Text>
            <Text className="cameras-section-subtitle">
              {loading ? "Atualizando..." : `${cameras.length} itens`}
            </Text>
          </HStack>

          {cameras.length === 0 && !loading ? (
            <Box className="cameras-empty">
              <Text>Nenhuma câmera vinculada ao usuário.</Text>
            </Box>
          ) : (
            <Stack spacing={3}>
              {cameras.map((cam) => {
                const isExpanded = expandedId === cam.id;
                return (
                  <Box key={cam.id} className="camera-item-card">
                    <Stack spacing={2.5}>
                      <HStack justify="space-between" align="center" spacing={2}>
                        <HStack spacing={2.5} align="center" minW={0}>
                          <Box className="camera-card-icon">
                            <FaCamera />
                          </Box>
                          <Stack spacing={0} minW={0}>
                            <Text className="camera-item-title" noOfLines={1}>
                              {cam.nome || `Câmera ${cam.canal || cam.id}`}
                            </Text>
                            <Text className="camera-item-meta" noOfLines={1}>
                              Canal {cam.canal || "--"} - ID {cam.id}
                            </Text>
                          </Stack>
                        </HStack>

                        <HStack spacing={1.5}>
                          <Badge className={Number(cam.ativa) === 1 ? "camera-state-open" : "camera-state-off"}>
                            {Number(cam.ativa) === 1 ? "Ativa" : "Inativa"}
                          </Badge>
                          <Button
                            size="xs"
                            className="camera-edit-toggle-btn"
                            rightIcon={isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                            onClick={() => toggleExpand(cam.id)}
                          >
                            Configurar
                          </Button>
                        </HStack>
                      </HStack>

                      <HStack spacing={2} className="camera-row-actions" align="stretch">
                        <Input
                          className="camera-rtmp-input"
                          value={cam.rtmp_link || ""}
                          isReadOnly
                          placeholder="RTMP indisponível"
                        />
                        <Button
                          leftIcon={<FaCopy />}
                          className="cameras-secondary-btn"
                          onClick={() => copyRtmp(cam.rtmp_link)}
                          minW="118px"
                        >
                          Copiar RTMP
                        </Button>
                      </HStack>

                      {isExpanded ? (
                        <Box className="camera-expand-panel">
                          <Stack spacing={3}>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                              <FormControl>
                                <FormLabel className="cameras-field-label">Nome da câmera</FormLabel>
                                <Input
                                  className="cameras-input"
                                  value={cam.nome || ""}
                                  onChange={(event) => handleChange(cam.id, "nome", event.target.value)}
                                />
                              </FormControl>
                              <FormControl display="flex" alignItems="center" mt={{ base: 0, md: 8 }}>
                                <FormLabel className="cameras-field-label" htmlFor={`ativa-${cam.id}`} mb="0">
                                  Câmera ativa
                                </FormLabel>
                                <Switch
                                  id={`ativa-${cam.id}`}
                                  isChecked={Number(cam.ativa) === 1}
                                  onChange={(event) =>
                                    handleChange(cam.id, "ativa", event.target.checked ? 1 : 0)
                                  }
                                />
                              </FormControl>
                            </SimpleGrid>

                            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                              <FormControl display="flex" alignItems="center" width="auto">
                                <FormLabel className="cameras-field-label" htmlFor={`publica-${cam.id}`} mb="0">
                                  Visível para vizinhos
                                </FormLabel>
                                <Switch
                                  id={`publica-${cam.id}`}
                                  isChecked={Number(cam.flag_publico) === 1}
                                  onChange={(event) =>
                                    handleChange(cam.id, "flag_publico", event.target.checked ? 1 : 0)
                                  }
                                />
                              </FormControl>
                              <FormControl display="flex" alignItems="center" width="auto">
                                <FormLabel className="cameras-field-label" htmlFor={`ia-${cam.id}`} mb="0">
                                  IA monitorando
                                </FormLabel>
                                <Switch
                                  id={`ia-${cam.id}`}
                                  isChecked={Number(cam.ia_ativo) === 1}
                                  onChange={(event) =>
                                    handleChange(cam.id, "ia_ativo", event.target.checked ? 1 : 0)
                                  }
                                />
                              </FormControl>
                            </SimpleGrid>

                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                              <FormControl>
                                <FormLabel className="cameras-field-label">Início IA</FormLabel>
                                <Input
                                  className="cameras-input"
                                  type="time"
                                  value={formatHora(cam.ia_hora_inicio)}
                                  onChange={(event) =>
                                    handleChange(cam.id, "ia_hora_inicio", event.target.value)
                                  }
                                  isDisabled={Number(cam.ia_ativo) !== 1}
                                />
                              </FormControl>
                              <FormControl>
                                <FormLabel className="cameras-field-label">Fim IA</FormLabel>
                                <Input
                                  className="cameras-input"
                                  type="time"
                                  value={formatHora(cam.ia_hora_fim)}
                                  onChange={(event) =>
                                    handleChange(cam.id, "ia_hora_fim", event.target.value)
                                  }
                                  isDisabled={Number(cam.ia_ativo) !== 1}
                                />
                              </FormControl>
                            </SimpleGrid>

                            <Button
                              leftIcon={<FaSave />}
                              className="camera-update-btn"
                              onClick={() => handleUpdate(cam)}
                              isLoading={savingId === cam.id}
                              alignSelf="flex-start"
                            >
                              Salvar câmera
                            </Button>
                          </Stack>
                        </Box>
                      ) : null}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default GerenciarCameras;


